import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateStoreWeeklyReport } from "../../../api/mutations";
import { useLanguage } from "../../../contexts/LanguageContext";

// Monday-morning email report on/off (store field weekly_report_enabled).
// Optimistic: the switch moves at once and snaps back if the save fails.
export default function WeeklyReportSwitch({ storeId, enabled, disabled = false }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [optimistic, setOptimistic] = useState(null);
  const on = optimistic ?? !!enabled;

  const save = useMutation({
    mutationFn: (value) =>
      updateStoreWeeklyReport({ storeId, weekly_report_enabled: value }),
    onMutate: (value) => setOptimistic(value),
    onError: () => setOptimistic(null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["stores"] });
      setOptimistic(null);
    },
  });

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled || !storeId || save.isPending}
      onClick={() => save.mutate(!on)}
      title={disabled ? t("dashboard.advanced.weeklyReport.adminOnly") : undefined}
      className="inline-flex min-h-11 items-center gap-2.5 rounded-[10px] border border-gray-200 bg-white px-3.5 text-sm font-medium text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span
        aria-hidden="true"
        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${on ? "bg-indigo-600" : "bg-gray-300"}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all ${on ? "left-[18px]" : "left-0.5"}`}
        />
      </span>
      {t("dashboard.advanced.weeklyReport.label")}
    </button>
  );
}
